import React from 'react'

import { Box, Card, CardContent, Divider, Skeleton, Stack } from '@mui/material'

import { BlueprintGamesDrilldownRowsSkeleton } from '../../features/market-analytics/components/BlueprintGamesDrilldownTable/BlueprintGamesDrilldownTable'
import { BlueprintHierarchyBar } from '../../features/market-analytics/components/BlueprintHierarchyBar/BlueprintHierarchyBar'
import { MarketAnalyticsFilterBarSkeleton } from '../../features/market-analytics/components/MarketAnalyticsFilterBar'
import { MonthlyTrendBarChartSkeleton } from '../../features/market-analytics/components/MonthlyTrendBarChartSkeleton/MonthlyTrendBarChartSkeleton'
import './MarketLevelBlueprintDetailPage.scss'

// Mirrors `GlossaryDescriptionCard` / `BlueprintScreenshotsCard`: a full-width
// card with a small section label and a few body lines (or an image-slider-shaped
// block).
const StackedCardSkeleton: React.FC<{ media?: boolean }> = ({ media }) => (
  <Card>
    <CardContent>
      <Skeleton variant="text" width="45%" sx={{ fontSize: '0.6875rem', maxWidth: 220 }} />
      {media ? (
        // Matches BlueprintScreenshotsCard's portrait slide height so the page
        // doesn't wobble when the real screenshots card swaps in.
        <Skeleton variant="rounded" sx={{ mt: 1, width: '100%', height: 340, borderRadius: '12px' }} />
      ) : (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="94%" />
          <Skeleton variant="text" width="88%" />
          <Skeleton variant="text" width="62%" />
        </Stack>
      )}
    </CardContent>
  </Card>
)

// Mirrors `LevelStatsCard`: window-toggle header row, four dense stat tiles,
// then the monthly-trend header (label + metric toggle) and the fixed-height
// trend bar chart (shared `MonthlyTrendBarChartSkeleton` = real chart footprint).
const AdoptionStatsCardSkeleton: React.FC = () => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Skeleton variant="text" width={160} sx={{ fontSize: '0.6875rem' }} />
        <Skeleton variant="rounded" width={168} height={28} sx={{ borderRadius: '32px' }} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" sx={{ height: 100, borderRadius: '16px' }} />
        ))}
      </Box>
      {/* Trend header: section label + Adoption/Density metric toggle */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 3, mb: 1.5 }}>
        <Skeleton variant="text" width={120} sx={{ fontSize: '0.6875rem' }} />
        <Skeleton variant="rounded" width={140} height={28} sx={{ borderRadius: '32px' }} />
      </Stack>
      <MonthlyTrendBarChartSkeleton />
    </CardContent>
  </Card>
)

// Mirrors `BlueprintGamesDrilldownTable`: header copy + the shared game-row stack.
const GamesDrilldownSkeleton: React.FC = () => (
  <Card>
    <CardContent sx={{ pb: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Skeleton variant="text" width="35%" sx={{ fontSize: '1.25rem', maxWidth: 320 }} />
      <Skeleton variant="text" width="50%" sx={{ fontSize: '0.875rem', mt: 0.5, maxWidth: 480 }} />
    </CardContent>
    <Divider />
    <BlueprintGamesDrilldownRowsSkeleton />
  </Card>
)

interface MarketLevelBlueprintDetailPageSkeletonProps {
  // Taxonomy selections carried in the URL, forwarded so the filter-bar
  // placeholder reserves the same chip rows + footer the resolved bar renders.
  selectedCategoryCount?: number
  selectedGenreCount?: number
  selectedSubgenreCount?: number
}

// Full-page skeleton for `MarketLevelBlueprintDetailPage` (v14 layout). Rendered
// while the small blueprint metadata query is in flight so the layout shape and
// vertical rhythm are in place before real data swaps in — no CLS and no big
// centered spinner (see GR theming rule: prefer Skeleton for content loading).
export const MarketLevelBlueprintDetailPageSkeleton: React.FC<MarketLevelBlueprintDetailPageSkeletonProps> = ({
  selectedCategoryCount = 0,
  selectedGenreCount = 0,
  selectedSubgenreCount = 0,
}) => {
  return (
    <Box className="MarketLevelBlueprintDetailPage" aria-busy="true">
      {/* Mirrors the real sticky hierarchy bar's shape/skeleton state so there's
          no layout shift once the blueprint metadata resolves. */}
      <BlueprintHierarchyBar isLoading />

      {/* Filter bar: same card/row footprint and position (directly under the
          hierarchy bar) as the resolved page's MarketAnalyticsFilterBar. */}
      <Box sx={{ mt: 1.5 }}>
        <MarketAnalyticsFilterBarSkeleton isActive={selectedCategoryCount + selectedGenreCount + selectedSubgenreCount > 0} />
      </Box>

      <Box sx={{ mb: 2.5 }}>
        <Skeleton variant="text" width="40%" sx={{ fontSize: '1.375rem', maxWidth: 420 }} />
      </Box>

      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <StackedCardSkeleton />
        <StackedCardSkeleton media />
        <AdoptionStatsCardSkeleton />
        <GamesDrilldownSkeleton />
      </Stack>
    </Box>
  )
}
